import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Check,
  X,
  ExternalLink,
  CircleSlash,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  useAnotador,
  crearAnotacion,
  activarAnotador,
} from "../lib/anotador";
import { useAnotadorSnapshot } from "../lib/anotadorContext";

function buildPayload(snapshot, veredicto, nota) {
  const base = snapshot ?? {};
  const intenciones = (base.intenciones ?? []).map((i) => ({
    area: i.area,
    query: i.query ?? null,
    jobcard_id: i.jobcard_id ?? null,
    cantidad: i.cantidad ?? null,
    unidad: i.unidad ?? null,
  }));
  const partidasFinales = (base.partidas ?? []).map((p) => ({
    jobcard_id: p.jobcard_id ?? null,
    area: p.area ?? null,
    concepto: p.concepto ?? null,
    cantidad: Number(p.cantidad) || 0,
    unidad: p.unidad ?? null,
    precio_unitario: Number(p.precio_unitario) || 0,
  }));
  const costeTotal = partidasFinales.reduce(
    (s, p) => s + (Number(p.cantidad) || 0) * (Number(p.precio_unitario) || 0),
    0,
  );
  return {
    veredicto,
    fase: base.fase ?? "step3_presupuesto",
    prompt: base.prompt ?? "",
    nota: nota?.trim() || null,
    escala: base.escala ?? null,
    tipo_proyecto: base.proyectoIntegral?.tipoProyecto ?? null,
    tipo_obra: base.proyectoIntegral?.obra ?? null,
    proyecto_integral: base.proyectoIntegral ?? null,
    intenciones,
    preguntas: base.preguntas ?? [],
    partidas_finales: partidasFinales,
    coste_total: Number.isFinite(costeTotal) ? costeTotal : null,
    autor: "anonimo",
  };
}

export default function AnnotatorPanel() {
  const activo = useAnotador();
  const snapshot = useAnotadorSnapshot();
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState("");
  const [enviando, setEnviando] = useState(null);
  const [ultima, setUltima] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!activo) {
      setAbierto(false);
    }
  }, [activo]);

  const promptResumen = useMemo(() => {
    const p = snapshot?.prompt ?? "";
    if (!p) return "";
    return p.length > 140 ? p.slice(0, 137) + "…" : p;
  }, [snapshot?.prompt]);

  const numIntenciones = (snapshot?.intenciones ?? []).length;
  const numPartidas = (snapshot?.partidas ?? []).length;
  const tienePrompt = !!(snapshot?.prompt && snapshot.prompt.trim());

  const enviar = async (veredicto) => {
    if (!tienePrompt) {
      toast.error("No hay prompt asociado para anotar");
      return;
    }
    setEnviando(veredicto);
    try {
      const payload = buildPayload(snapshot, veredicto, nota);
      const data = await crearAnotacion(payload);
      setUltima({ veredicto, id: data?.id });
      setNota("");
      toast.success(
        veredicto === "bueno"
          ? "Marcado como CORRECTO. Servirá de ejemplo en futuras IA."
          : "Marcado como MALO. Se evitará repetir este patrón.",
      );
    } catch (err) {
      console.error("anotacion_error", err);
      toast.error(err?.response?.data?.message || "No se pudo guardar la anotación");
    } finally {
      setEnviando(null);
    }
  };

  if (!activo) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[60] w-[360px] max-w-[calc(100vw-32px)] font-sans">
      <div className="rounded-xl border border-amber-300 bg-white shadow-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 hover:bg-amber-100 transition-colors text-amber-900 text-left"
          data-testid="annotator-toggle"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider">
              A
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest font-bold leading-none">
                Modo anotador
              </div>
              <div className="text-[11px] text-amber-700/80 mt-0.5 truncate">
                {tienePrompt
                  ? `${numIntenciones} intenciones · ${numPartidas} partidas`
                  : "Sin contexto activo"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to="/anotaciones"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded hover:bg-amber-200 text-amber-800"
              title="Abrir panel admin"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            {abierto ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </div>
        </button>

        {abierto ? (
          <div className="p-3 space-y-3 border-t border-amber-200/70">
            <div className="text-[11px] text-slate-600 leading-snug">
              {tienePrompt ? (
                <>
                  <div className="font-bold uppercase tracking-widest text-[10px] text-slate-500 mb-1">
                    Prompt actual
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-2 text-slate-700">
                    {promptResumen}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-slate-500">
                  <CircleSlash className="w-3.5 h-3.5" />
                  Genera un presupuesto y vuelve para anotarlo.
                </div>
              )}
            </div>

            <div>
              <Textarea
                ref={inputRef}
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Nota opcional (qué falla / por qué es bueno)"
                className="text-xs min-h-[64px] resize-none"
                data-testid="annotator-nota"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={() => enviar("malo")}
                disabled={!tienePrompt || enviando !== null}
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
                data-testid="annotator-malo"
              >
                {enviando === "malo" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsDown className="w-4 h-4" />
                )}
                Marcar malo
              </Button>
              <Button
                type="button"
                onClick={() => enviar("bueno")}
                disabled={!tienePrompt || enviando !== null}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="annotator-bueno"
              >
                {enviando === "bueno" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsUp className="w-4 h-4" />
                )}
                Marcar bueno
              </Button>
            </div>

            {ultima ? (
              <div
                className={`flex items-center gap-2 text-[11px] rounded-md px-2 py-1.5 ${
                  ultima.veredicto === "bueno"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                <Check className="w-3.5 h-3.5" />
                Última anotación: {ultima.veredicto.toUpperCase()} · id{" "}
                <code className="font-mono">{ultima.id}</code>
              </div>
            ) : null}

            <div className="flex items-center justify-between pt-1 border-t border-slate-200/70">
              <Link
                to="/anotaciones"
                className="text-[11px] text-blue-700 hover:underline"
              >
                Ver todas las anotaciones
              </Link>
              <button
                type="button"
                onClick={() => activarAnotador(false)}
                className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                title="Salir del modo anotador"
              >
                <X className="w-3 h-3" /> Salir
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
